import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getAccessToken, setAccessToken, setLogoutHandler, doRefresh } from "@/lib/apiClient";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out, obj = logged in
  const [ready, setReady] = useState(false);

  const logout = useCallback(async () => {
    const token = getAccessToken();
    setAccessToken(null);
    setUser(false);
    try { await api.post("/auth/logout", {}, { headers: token ? { Authorization: `Bearer ${token}` } : {} }); } catch (_) {}
  }, []);

  useEffect(() => {
    setLogoutHandler(() => setUser(false));
    (async () => {
      try {
        const data = await doRefresh(); // uses HttpOnly refresh cookie
        setAccessToken(data.access_token);
        setUser(data.user);
      } catch (_) {
        if (!getAccessToken()) setUser(false);
      } finally {
        setReady(true);
      }
    })();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setAccessToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    setAccessToken(data.access_token);
    setUser(data.user);
    return { ...data.user, verification_email_sent: data.verification_email_sent };
  };

  const refreshMe = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, register, logout, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}
